import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ShieldCheck, AlertTriangle, CheckCircle, Clock, Plus,
  Loader2, X, Sparkles, Search, FileText,
} from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { apiGet, apiPost } from '@/lib/api'
import { cn, formatDate } from '@/lib/utils'
import { ComplianceDocuments } from '@/components/DocumentPanel'
import { DocumentViewer } from '@/components/DocumentViewer'

interface Certificate {
  id: string
  certificateNumber: string | null
  issuedBy: string | null
  issuedAt: string
  expiresAt: string
  documentUrl: string | null  // DocFlow document ID
  notes: string | null
  status: string
  daysUntilExpiry: number
  isExpired: boolean
  isDueSoon: boolean
  category: { id: string; name: string; code: string; regulation: string | null }
}

export function CompliancePage() {
  const { currentPropertyId } = useAuthStore()
  const pid = currentPropertyId
  const queryClient = useQueryClient()

  const [filterCategory, setFilterCategory] = useState('')
  const [showExpiredOnly, setShowExpiredOnly] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)

  const [newCert, setNewCert] = useState({
    categoryId: '', certificateNumber: '', issuedBy: '',
    issuedAt: '', expiresAt: '', notes: '',
  })

  // ── Queries ─────────────────────────────────────────────────────────────
  const { data: certificates, isLoading } = useQuery({
    queryKey: ['compliance-certs', pid, filterCategory],
    queryFn: () =>
      apiGet<Certificate[]>(
        `/properties/${pid}/compliance/certificates`,
        filterCategory ? { categoryId: filterCategory } : {}
      ),
    enabled: !!pid,
  })

  const { data: categories } = useQuery({
    queryKey: ['compliance-categories'],
    queryFn: () => apiGet<any[]>(`/properties/${pid}/ppm/compliance-summary`),
    enabled: !!pid,
  })

  const addMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost(`/properties/${pid}/compliance/certificates`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance-certs'] })
      setShowAddModal(false)
      setNewCert({ categoryId: '', certificateNumber: '', issuedBy: '', issuedAt: '', expiresAt: '', notes: '' })
    },
  })

  const [scanResult, setScanResult] = useState<{ scanned: number; classified: number; certificatesCreated: number; results: any[] } | null>(null)

  const scanMutation = useMutation({
    mutationFn: () => apiPost('/docflow/scan-compliance', {}),
    onSuccess: (data: any) => {
      setScanResult(data)
      queryClient.invalidateQueries({ queryKey: ['compliance-certs'] })
      queryClient.invalidateQueries({ queryKey: ['docflow-compliance'] })
    },
  })

  function handleAddSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!newCert.categoryId || !newCert.issuedAt || !newCert.expiresAt) return
    addMutation.mutate({
      categoryId: newCert.categoryId,
      ...(newCert.certificateNumber && { certificateNumber: newCert.certificateNumber }),
      ...(newCert.issuedBy && { issuedBy: newCert.issuedBy }),
      issuedAt: new Date(newCert.issuedAt).toISOString(),
      expiresAt: new Date(newCert.expiresAt).toISOString(),
      ...(newCert.notes && { notes: newCert.notes }),
    })
  }

  const [viewDoc, setViewDoc] = useState<{ docId: string; title: string } | null>(null)

  const certs = certificates ?? []
  const filtered = showExpiredOnly ? certs.filter(c => c.isExpired) : certs
  const expiredCount = certs.filter(c => c.isExpired).length
  const dueSoonCount = certs.filter(c => c.isDueSoon).length
  const compliantCount = certs.filter(c => !c.isExpired && !c.isDueSoon).length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-800">Compliance</h1>
          <p className="text-gray-500 text-sm mt-0.5">Certificates and regulatory tracking</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => scanMutation.mutate()}
            disabled={scanMutation.isPending}
            className="btn-secondary"
          >
            {scanMutation.isPending ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Scanning...</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Scan DocFlow</>
            )}
          </button>
          <button onClick={() => setShowAddModal(true)} className="btn-primary">
            <Plus className="w-4 h-4" />
          Add Certificate
        </button>
        </div>
      </div>

      {/* Scan results */}
      {scanResult && (
        <div className="card border-purple-200 bg-purple-50/30">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-navy-800 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-500" />
              DocFlow Scan Results
            </h3>
            <button onClick={() => setScanResult(null)} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-gray-600">
            Scanned {scanResult.scanned} documents, classified {scanResult.classified} as compliance.
            {scanResult.certificatesCreated > 0 && (
              <span className="text-green-600 font-medium"> Created {scanResult.certificatesCreated} certificates.</span>
            )}
          </p>
          {scanResult.results.length > 0 && (
            <div className="mt-2 space-y-1">
              {scanResult.results.map((r: any, i: number) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <CheckCircle className="w-3 h-3 text-green-500" />
                  <span className="text-navy-800">{r.title || r.docId.slice(0, 8)}</span>
                  <span className="badge bg-purple-100 text-purple-800">{r.categoryCode}</span>
                  <span className="text-gray-400">{Math.round(r.confidence * 100)}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card py-4">
          <p className="text-xs text-gray-400">Total Certificates</p>
          <p className="text-2xl font-bold text-navy-800">{certs.length}</p>
        </div>
        <div className="card py-4 border-green-200">
          <p className="text-xs text-gray-400">Compliant</p>
          <p className="text-2xl font-bold text-green-600">{compliantCount}</p>
        </div>
        <div className="card py-4 border-amber-200">
          <p className="text-xs text-gray-400">Expiring Soon</p>
          <p className={cn('text-2xl font-bold', dueSoonCount > 0 ? 'text-amber-600' : 'text-navy-800')}>
            {dueSoonCount}
          </p>
        </div>
        <div className="card py-4 border-red-200">
          <p className="text-xs text-gray-400">Expired</p>
          <p className={cn('text-2xl font-bold', expiredCount > 0 ? 'text-red-600' : 'text-navy-800')}>
            {expiredCount}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="input w-auto min-w-[180px]"
        >
          <option value="">All Categories</option>
          {categories?.map((cat: any) => (
            <option key={cat.id} value={cat.id}>{cat.name} ({cat.code})</option>
          ))}
        </select>

        <button
          onClick={() => setShowExpiredOnly(!showExpiredOnly)}
          className={cn('btn-secondary text-xs', showExpiredOnly && 'border-red-400 text-red-600 bg-red-50')}
        >
          <AlertTriangle className="w-3.5 h-3.5" />
          Expired only
        </button>
      </div>

      {/* Certificates */}
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-800" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-16">
          <ShieldCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">
            {showExpiredOnly ? 'No expired certificates' : 'No certificates found'}
          </p>
          <p className="text-xs text-gray-400 mt-1">Add certificates using the button above</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((cert) => {
            const isDocFlowLinked = !!cert.documentUrl
            const docFlowDocId = cert.documentUrl
            return (
              <div
                key={cert.id}
                className={cn('card', {
                  'border-green-200': !cert.isExpired && !cert.isDueSoon,
                  'border-amber-200 bg-amber-50/20': cert.isDueSoon,
                  'border-red-300 bg-red-50/30': cert.isExpired,
                })}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-navy-800">{cert.category.name}</h3>
                    <p className="text-xs text-gray-400 font-mono">{cert.category.code}</p>
                  </div>
                  {cert.isExpired ? (
                    <span className="badge bg-red-100 text-red-800">Expired</span>
                  ) : cert.isDueSoon ? (
                    <span className="badge bg-amber-100 text-amber-800">Expiring Soon</span>
                  ) : (
                    <span className="badge bg-green-100 text-green-800">Compliant</span>
                  )}
                </div>

                {cert.category.regulation && (
                  <p className="text-xs text-gray-500 mb-3 leading-relaxed">{cert.category.regulation}</p>
                )}

                <div className="space-y-2 text-sm">
                  {cert.certificateNumber && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Certificate #</span>
                      <span className="text-navy-800 font-mono text-xs">{cert.certificateNumber}</span>
                    </div>
                  )}
                  {cert.issuedBy && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Issued By</span>
                      <span className="text-navy-800">{cert.issuedBy}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Issued</span>
                    <span className="text-navy-800">{formatDate(cert.issuedAt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Expires</span>
                    <span className={cn('font-medium',
                      cert.isExpired ? 'text-red-600' : cert.isDueSoon ? 'text-amber-600' : 'text-navy-800'
                    )}>
                      {formatDate(cert.expiresAt)}
                    </span>
                  </div>
                </div>

                <div className={cn(
                  'mt-3 pt-3 border-t border-gray-100 flex items-center justify-between',
                )}>
                  <div className={cn('flex items-center gap-2',
                    cert.isExpired ? 'text-red-600' : cert.isDueSoon ? 'text-amber-600' : 'text-gray-500'
                  )}>
                    {cert.isExpired ? (
                      <AlertTriangle className="w-4 h-4" />
                    ) : cert.isDueSoon ? (
                      <Clock className="w-4 h-4" />
                    ) : (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    )}
                    <span className="text-xs font-medium">
                      {cert.isExpired
                        ? `Expired ${Math.abs(cert.daysUntilExpiry)} days ago`
                        : `${cert.daysUntilExpiry} days remaining`}
                    </span>
                  </div>
                  {isDocFlowLinked && (
                    <button
                      type="button"
                      onClick={() => setViewDoc({ docId: docFlowDocId!, title: cert.category.name })}
                      className="text-xs text-navy-600 hover:text-navy-800 flex items-center gap-1"
                    >
                      <FileText className="w-3 h-3" /> View Document
                    </button>
                  )}
                </div>

                {/* Extract metadata button */}
                {isDocFlowLinked && (
                  <ExtractMetadataButton certificateId={cert.id} documentId={docFlowDocId!} />
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Document Viewer */}
      {viewDoc && (
        <DocumentViewer
          docId={viewDoc.docId}
          title={viewDoc.title}
          onClose={() => setViewDoc(null)}
        />
      )}

      {/* Add Certificate Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-navy-800 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-purple-600" />
                Add Certificate
              </h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddSubmit} className="space-y-4">
              <div>
                <label className="label">Category *</label>
                <select
                  value={newCert.categoryId}
                  onChange={(e) => setNewCert({ ...newCert, categoryId: e.target.value })}
                  className="input"
                  required
                >
                  <option value="">Select category...</option>
                  {categories?.map((cat: any) => (
                    <option key={cat.id} value={cat.id}>{cat.name} ({cat.code})</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Certificate Number</label>
                  <input
                    value={newCert.certificateNumber}
                    onChange={(e) => setNewCert({ ...newCert, certificateNumber: e.target.value })}
                    className="input"
                    placeholder="e.g. LOLER-2026-001"
                  />
                </div>
                <div>
                  <label className="label">Issued By</label>
                  <input
                    value={newCert.issuedBy}
                    onChange={(e) => setNewCert({ ...newCert, issuedBy: e.target.value })}
                    className="input"
                    placeholder="e.g. Bureau Veritas"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Issue Date *</label>
                  <input
                    type="date"
                    value={newCert.issuedAt}
                    onChange={(e) => setNewCert({ ...newCert, issuedAt: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Expiry Date *</label>
                  <input
                    type="date"
                    value={newCert.expiresAt}
                    onChange={(e) => setNewCert({ ...newCert, expiresAt: e.target.value })}
                    className="input"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="label">Notes</label>
                <textarea
                  value={newCert.notes}
                  onChange={(e) => setNewCert({ ...newCert, notes: e.target.value })}
                  rows={2}
                  className="input resize-none"
                  placeholder="Any additional notes..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newCert.categoryId || !newCert.issuedAt || !newCert.expiresAt || addMutation.isPending}
                  className="btn-primary"
                >
                  {addMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Adding...</>
                  ) : (
                    <><Plus className="w-4 h-4" /> Add Certificate</>
                  )}
                </button>
              </div>

              {addMutation.isError && (
                <p className="text-sm text-red-600">Failed to add certificate. Please try again.</p>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Extract Metadata Button ─────────────────────────────────────────────────
function ExtractMetadataButton({ certificateId, documentId }: { certificateId: string; documentId: string }) {
  const queryClient = useQueryClient()
  const [result, setResult] = useState<any>(null)

  const extractMutation = useMutation({
    mutationFn: () => apiPost('/docflow/extract-cert-metadata', { certificateId, documentId }),
    onSuccess: (data: any) => {
      setResult(data)
      queryClient.invalidateQueries({ queryKey: ['compliance-certs'] })
    },
  })

  return (
    <div className="mt-2">
      {!result ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); extractMutation.mutate() }}
          disabled={extractMutation.isPending}
          className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"
        >
          {extractMutation.isPending ? (
            <><Loader2 className="w-3 h-3 animate-spin" /> Reading document...</>
          ) : (
            <><Sparkles className="w-3 h-3" /> Extract details with AI</>
          )}
        </button>
      ) : (
        <div className="text-xs text-green-600 flex items-center gap-1 mt-1">
          <CheckCircle className="w-3 h-3" /> Updated {result.updated?.length ?? 0} fields
        </div>
      )}
      {extractMutation.isError && (
        <p className="text-xs text-red-500 mt-1">Extraction failed — document may not have text content yet</p>
      )}
    </div>
  )
}
